import { Banner, LoadingSpinner, TextLink, Typography } from '@neo4j-ndl/react';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { getFlag } from '@nx/launch-darkly-service';
import { useLogger } from '@nx/logger/react';
import type { Instance, TierConfig } from '@nx/state';
import {
  MODAL_TYPE,
  NEO4J_MANAGED_KEY,
  PLAN_TYPE,
  PROJECT_BILLING_METHOD,
  TIER,
  consoleApi,
  getApiError,
  getApiErrorMessage,
  getErrorMessageFromUnknown,
  useActiveOrg,
  useActiveProject,
  useModal,
  useNotifications,
} from '@nx/state';
import { type Validation, isNotNullish, isNullish, isValid } from '@nx/stdlib';
import { Center, Page } from '@nx/ui';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { matchPath, useNavigate } from 'react-router';
import type { SerializedError } from 'vitest';

import { StripeFormFallback } from '../../../loading-fallbacks';
import type { BillingFormData } from '../../../project/billing/shared/billing-form-data';
import { defaults as billingDefaults } from '../../../project/billing/shared/helpers';
import { useTrackUpxEvent } from '../../../services/segment/analytics';
import { NewInstanceForm } from '../../create/new-instance-form';
import { getDefaultProduct, getDefaultTier, tierConfigForClone, validate } from '../../entities/helpers';
import type { InstanceFormData, Product } from '../../entities/model';
import { BillingSection } from '../../form-components/billing-section';
import { InstanceCreateFooter } from '../../form-components/footer';
import { ProductSelection } from '../../form-components/product-selection';
import { defaults } from './helpers';

type CloneToNewInstancePageProps = {
  instance: Instance;
  tierConfig: TierConfig;
};
type Step = 'instance-details' | 'billing';

const CloneToNewInstancePage = ({ instance, tierConfig: defaultTierConfig }: CloneToNewInstancePageProps) => {
  const logger = useLogger();
  const activeProject = useActiveProject();
  const navigate = useNavigate();
  const trackEvent = useTrackUpxEvent();
  const notify = useNotifications();
  const createdInstanceModal = useModal(MODAL_TYPE.INSTANCE_CREATED);
  const cloningStoreFormatOptions = getFlag('cloningStoreFormatOptions', false);
  const allowCloningInstanceBetweenCsp = getFlag('plgCloningInstanceBetweenCsps', false);
  const { hasBilling, requiresBilling, tierConfigs } = activeProject;

  const [step, setStep] = useState<Step>('instance-details');
  const [product, setProduct] = useState<Product>(getDefaultProduct(tierConfigs));
  const [isServiceAddress, setIsServiceAddress] = useState(true);

  const [createInstance, createInstanceRes] = consoleApi.useCreateInstanceMutation();
  const [createAuraInstance] = consoleApi.useCreateAuraInstanceMutation();
  const defaultTier = getDefaultTier(activeProject.planType === PLAN_TYPE.SELF_SERVE, product);

  const [data, setData] = useState<InstanceFormData>(
    defaults(instance, defaultTier, tierConfigs[defaultTier] ?? defaultTierConfig, activeProject),
  );
  const targetTierConfiguration = useMemo(
    () =>
      tierConfigForClone({
        tierConfigs,
        instance,
        targetTier: data.tier,
        enableCrossCspCloning: allowCloningInstanceBetweenCsp,
      }) ?? undefined,
    [tierConfigs, instance, data.tier, allowCloningInstanceBetweenCsp],
  );

  const [billingData, setBillingData] = useState(billingDefaults());
  const [submitBilling, setSubmitBillingFunc] =
    useState<(data: BillingFormData, isServiceAddress?: boolean) => Promise<void>>();

  const showBilling = requiresBilling && !hasBilling;

  const handleProductSelect = (newProduct: Product) => {
    if (activeProject.planType === PLAN_TYPE.ENTERPRISE) {
      const tier = newProduct === 'AuraDB' ? TIER.ENTERPRISE : TIER.AURA_DSE;
      const tierConfig =
        tierConfigForClone({
          tierConfigs,
          instance,
          targetTier: tier,
          enableCrossCspCloning: allowCloningInstanceBetweenCsp,
        }) ?? tierConfigs[tier];
      if (isNullish(tierConfig)) {
        return;
      }
      const newData = defaults(instance, tier, tierConfig, activeProject);

      setData((oldData) => ({ ...newData, name: oldData.name }));
    } else {
      const tier = newProduct === 'AuraDB' ? TIER.PROFESSIONAL : TIER.GDS;
      const tierConfig =
        tierConfigForClone({
          tierConfigs,
          instance,
          targetTier: tier,
          enableCrossCspCloning: allowCloningInstanceBetweenCsp,
        }) ?? tierConfigs[tier];
      if (isNullish(tierConfig)) {
        return;
      }
      const newData = defaults(instance, tier, tierConfig, activeProject);
      setData((oldData) => ({ ...newData, name: oldData.name }));
    }
    setProduct(newProduct);
  };

  if (isNullish(defaultTierConfig)) {
    throw new Error(`TierConfig for ${instance.tier} is missing.`);
  }
  const [validation, setValidation] = useState<Validation<InstanceFormData> | null>(null);

  const handleSelectTier = (tier: TIER) => {
    setValidation(null);
    const tierConfig =
      tierConfigForClone({
        tierConfigs,
        instance,
        targetTier: tier,
        enableCrossCspCloning: allowCloningInstanceBetweenCsp,
      }) ??
      tierConfigs[tier] ??
      defaultTierConfig;
    const newData = defaults(instance, tier, tierConfig, activeProject);
    setData(newData);
  };

  const handleCreateInstance = async (formData: InstanceFormData) => {
    const validationError = validate({ ...formData, confirmed: true });
    if (!isValid(formData, validationError)) {
      setValidation(validationError);
      return;
    }
    if (requiresBilling && !hasBilling && step === 'billing') {
      try {
        await submitBilling?.(billingData, isServiceAddress);
      } catch (e: unknown) {
        const message = getErrorMessageFromUnknown(e);
        notify.addNotification({
          title: 'Failed to add billing',
          description: message,
          type: 'danger',
          timeout: 5000,
        });
        logger.error(message);
        return;
      }
    }
    setValidation(null);

    const payloadData = {
      tier: formData.tier,
      region: formData.region,
      version: formData.version,
      cloudProvider: formData.cloudProvider,
      name: formData.name,
      memory: formData.size.memory,
      storage: formData.size.storage,
      projectId: activeProject.id,
      organizationId: activeProject.organizationId,
      sourceSnapshot: {
        dbId: instance.id,
        // Snapshot ID to be added
        // ...(isNotNullish(snapshotId) ? { snapshotId } : {}),
      },
      ...(activeProject.capabilities.cmek &&
        [TIER.ENTERPRISE, TIER.AURA_DSE].includes(formData.tier) && {
          encryptionKeyRef: formData.encryptionKeyRef === NEO4J_MANAGED_KEY ? undefined : formData.encryptionKeyRef,
        }),
      isTrial: false,
      migrateToBlockFormat:
        isNotNullish(data.migrateToBlock) && cloningStoreFormatOptions ? data.migrateToBlock : false,
    };

    const multiDbEnterprise =
      activeProject.capabilities.multi_db_enterprise && [TIER.ENTERPRISE, TIER.MTE].includes(formData.tier);

    const createMutation = multiDbEnterprise ? createAuraInstance : createInstance;

    createMutation(payloadData)
      .unwrap()
      .then((result) => {
        createdInstanceModal.open({ createdInstance: result });

        trackEvent({
          event: AURA_CONSOLE_EVENTS.INSTANCE_CLONE_TO_NEW,
          properties: {
            sourceTier: instance.tier,
            targetTier: formData.tier,
          },
          scope: APP_SCOPE.aura,
        });
        void navigate(`/projects/${activeProject.id}/instances`);
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const message = getApiErrorMessage(e);
        notify.addNotification({
          title: 'Failed to create instance',
          description: message,
          type: 'danger',
          timeout: 5000,
        });
        logger.error(message);
      });
  };

  const handleDataChange = useCallback(
    (newData: Partial<InstanceFormData>) => {
      if (isNotNullish(validation)) {
        setValidation(null);
      }
      setData((oldData) => ({ ...oldData, ...newData }));
    },
    [validation],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex w-full grow flex-col overflow-auto">
        <Page contentType="light" className="@container" data-testid="clone-to-new-instance-page">
          <div
            className={classNames('flex flex-col gap-2', {
              'gap-4': step === 'billing',
            })}
          >
            <Typography variant="h2" className="flex items-center justify-between">
              Clone instance
              {step === 'instance-details' && (
                <ProductSelection
                  tierConfigs={tierConfigs}
                  handleProductSelect={handleProductSelect}
                  product={product}
                />
              )}
            </Typography>

            {step === 'instance-details' && (
              <Typography variant="body-medium" className="text-neutral-text-weak flex-wrap">
                Select a tier to get started. We'll recommend default values to help you get going.{' '}
                <TextLink
                  href={`https://neo4j.com/pricing/#${product === 'AuraDS' ? 'graph-analytics' : 'graph-database'}`}
                  className="no-underline"
                  type="external"
                >
                  See full feature comparison
                </TextLink>
              </Typography>
            )}
            {step === 'billing' && <Typography variant="h5">Add payment method</Typography>}
          </div>
          {step === 'instance-details' && (
            <NewInstanceForm
              data={data}
              onDataChange={handleDataChange}
              instance={instance}
              project={activeProject}
              validation={validation}
              product={product}
              onSelectTier={handleSelectTier}
              tierConfig={targetTierConfiguration}
              showFreeTier={false}
            />
          )}
          {step === 'billing' && (
            <Suspense fallback={<StripeFormFallback />}>
              <BillingSection
                data={billingData}
                onDataChange={(newData) => setBillingData((prev) => ({ ...prev, ...newData }))}
                registerSubmit={(func) => setSubmitBillingFunc(() => func)}
                isServiceAddress={isServiceAddress}
                onIsServiceAddressChange={setIsServiceAddress}
              />
            </Suspense>
          )}
        </Page>
        {/* This is to add padding to the bottom since regular padding didn't work. */}
        <div className="!h-6" />
      </div>
      <InstanceCreateFooter
        confirmText={step === 'instance-details' && showBilling ? 'Continue' : 'Create Instance'}
        cancelText={step === 'instance-details' ? 'Cancel' : 'Back'}
        size={data.size}
        onConfirm={() => {
          if (step === 'instance-details' && showBilling && data.size?.isTrial === false && data.tier !== TIER.FREE) {
            const validationError = validate({ ...data, confirmed: true });
            if (!isValid(data, validationError)) {
              setValidation(validationError);
              return;
            }
            setStep('billing');
          } else {
            void handleCreateInstance(data);
          }
        }}
        isLoading={createInstanceRes.isLoading}
        isDisabled={data.size?.isPrepaidOnly === true && activeProject.billingMethod !== PROJECT_BILLING_METHOD.PREPAID}
        onCancel={() => {
          if (step === 'billing') {
            setStep('instance-details');
          } else {
            void navigate(`/projects/${activeProject.id}/instances`);
          }
        }}
        project={activeProject}
      />
    </div>
  );
};

export const CloneToNewInstancePageGuarded = () => {
  const instanceId = matchPath('/projects/:projectId/instances/clone-to-new/:instanceId', location.pathname)?.params
    .instanceId;
  const project = useActiveProject();
  const navigate = useNavigate();
  const org = useActiveOrg();
  const allowCloningInstanceBetweenCsp = getFlag('plgCloningInstanceBetweenCsps', false);

  const {
    data: instance,
    isLoading,
    isError,
    error,
  } = consoleApi.useGetInstanceQuery(instanceId ?? skipToken, { pollingInterval: 15000 });

  if (!org.featureToggles['plg-simplify-instance-creation']) {
    void navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }
  if (isError || isNullish(instance)) {
    const apiError = getApiError(error);
    return (
      <Page contentType="light">
        <Banner
          description={apiError.message ?? `Instance not found: ${instanceId}`}
          title="Error"
          type="danger"
          htmlAttributes={{ 'data-testid': 'clone-to-new-instance-error-banner' }}
        />
      </Page>
    );
  }

  const tierConfig = tierConfigForClone({
    tierConfigs: project.tierConfigs,
    instance,
    targetTier: instance.tier,
    enableCrossCspCloning: allowCloningInstanceBetweenCsp,
  });

  if (isNullish(tierConfig)) {
    return (
      <Page contentType="light">
        <Banner
          title="Error"
          description="The instance is currently not cloneable."
          type="danger"
          htmlAttributes={{ 'data-testid': 'clone-to-new-instance-error-banner' }}
        />
      </Page>
    );
  }
  return <CloneToNewInstancePage instance={instance} tierConfig={tierConfig} />;
};
